import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrder = params.get("paypal");
  const orderRef = sessionId || paypalOrder;
  const statusUrl = paypalOrder ? `/payments/paypal/status/${paypalOrder}` : `/payments/status/${sessionId}`;
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [expired, setExpired] = useState(false);
  const cart = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!orderRef) return;
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      if (count >= 8) { setExpired(true); return; }
      count += 1; setAttempts(count);
      try {
        const { data } = await api.get(statusUrl);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) { cart.clear(); clearedRef.current = true; }
          return;
        }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderRef]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-bone px-6" data-testid="page-payment-success">
      <div className="max-w-xl w-full bg-white border border-hair p-10 md:p-14 text-center">
        {!paid && !expired && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-gold" />
            <div className="eyebrow mt-6">Verifica pagamento</div>
            <h1 className="font-serif text-3xl mt-3">Un attimo…</h1>
            <p className="text-taupe mt-4 text-sm">Stiamo confermando l'avvenuto pagamento. Tentativo {attempts}/8.</p>
          </>
        )}
        {paid && (
          <>
            <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto">
              <Check size={30} className="text-gold" strokeWidth={2} />
            </div>
            <div className="eyebrow mt-6">Ordine confermato</div>
            <h1 className="font-serif text-4xl mt-3">Grazie!</h1>
            <p className="text-taupe mt-4">
              Riceverai a breve un'email di conferma. Ti aggiorneremo su ogni fase, dalla spedizione alla consegna.
            </p>
            {status.amount != null && (
              <div className="mt-8 border-t border-hair pt-6 text-left space-y-2">
                {(status.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{it.quantity}× {it.name || it.product_name}</span>
                    <span className="text-taupe">{eur(it.price * it.quantity)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-hair pt-4 items-baseline">
                  <span className="eyebrow">Totale</span>
                  <span className="font-serif text-2xl">{eur(status.amount)}</span>
                </div>
              </div>
            )}
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              <Link to="/shop" className="btn-outline-ink" data-testid="success-back-shop">Continua lo shopping</Link>
              <Link to="/" className="btn-ink" data-testid="success-go-home">Torna alla home</Link>
            </div>
          </>
        )}
        {expired && !paid && (
          <>
            <div className="w-16 h-16 rounded-full bg-ink/5 flex items-center justify-center mx-auto">
              <X size={26} className="text-taupe" />
            </div>
            <div className="eyebrow mt-6 text-taupe">In attesa</div>
            <h1 className="font-serif text-3xl mt-3">Non abbiamo ancora conferma del pagamento.</h1>
            <p className="text-taupe mt-4 text-sm">
              Se hai completato il pagamento, riceverai comunque un'email di conferma. Puoi chiudere questa pagina.
            </p>
            <Link to="/shop" className="btn-outline-ink mt-8 inline-flex" data-testid="success-fallback-shop">Torna allo shop</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-bone px-6" data-testid="page-payment-cancel">
      <div className="max-w-xl w-full bg-white border border-hair p-10 md:p-14 text-center">
        <div className="w-16 h-16 rounded-full bg-ink/5 flex items-center justify-center mx-auto">
          <X size={26} className="text-taupe" />
        </div>
        <div className="eyebrow mt-6 text-taupe">Pagamento annullato</div>
        <h1 className="font-serif text-3xl mt-3">Il pagamento non è stato completato.</h1>
        <p className="text-taupe mt-4">I prodotti sono ancora nel tuo carrello. Puoi riprovare quando vuoi.</p>
        <Link to="/shop" className="btn-ink mt-8 inline-flex" data-testid="cancel-back-shop">Torna allo shop</Link>
      </div>
    </div>
  );
}
